import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  VehicleRental,
  VehicleRentalItem,
  VehicleRentalHistory,
  VehicleRentalAttachment,
  VehicleRentalHistoryDTO,
  LogVehicleRentalActionRequest,
  LogVehicleRentalStatusChangeRequest,
  VehicleRentalAttachmentDTO,
  CreateVehicleRentalAttachmentRequest,
  UpdateVehicleRentalAttachmentRequest,
  UploadFileRequest,
  AttachmentCategory,
  CreateVehicleRentalRequest,
  CreateVehicleRentalItemRequest,
  UpdateVehicleRentalRequest,
  StartRentalRequest,
  EndRentalRequest,
  CancelRentalRequest,
  VehicleRentalFilter,
  VehicleRentalStatistics
} from '../models/VehicleRental';

export interface VehicleRentalPagedResult {
  items: VehicleRental[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleRentalService {
  private readonly baseUrl = environment.microservices.rental || environment.apiUrl || '/api';
  private readonly apiUrl = `${this.baseUrl}/vehicle-rental`;
  private readonly historyApiUrl = `${this.baseUrl}/vehicle-rental-history`;
  private readonly attachmentApiUrl = `${this.baseUrl}/vehicle-rental-attachment`;
  private readonly blobApiUrl = `${this.baseUrl}/blobrental`;
  private readonly itemApiUrl = `${this.baseUrl}/vehicle-rental-item`;

  private vehicleRentalsSubject = new BehaviorSubject<VehicleRental[]>([]);
  public vehicleRentals$ = this.vehicleRentalsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ===== OPERAÇÕES BÁSICAS =====

  /**
   * Obtém todas as locações com paginação e metadados
   * Suporta filtros: status, priority, search
   */
  getAllVehicleRentalsPaginated(
    page: number = 1, 
    pageSize: number = 10,
    filters?: { status?: string; priority?: string; search?: string }
  ): Observable<VehicleRentalPagedResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    // Adicionar filtros opcionais
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.priority) {
      params = params.set('priority', filters.priority);
    }
    if (filters?.search) {
      params = params.set('search', filters.search);
    }

    return this.http.get<any>(this.apiUrl, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.vehicleRentalsSubject.next(result.items))
    );
  }

  /**
   * Compatibilidade: retorna somente os itens (sem metadados)
   */
  getAllVehicleRentals(): Observable<VehicleRental[]> {
    return this.getAllVehicleRentalsPaginated().pipe(map(r => r.items));
  }

  /**
   * Obtém uma locação por ID
   */
  getVehicleRentalById(id: number): Observable<VehicleRental> {
    return this.http.get<VehicleRental>(`${this.apiUrl}/${id}`).pipe(
      map(rental => this.mapDates(rental))
    );
  }

  /**
   * Obtém uma locação por número
   */
  getVehicleRentalByNumber(rentalNumber: string): Observable<VehicleRental> {
    return this.http.get<VehicleRental>(`${this.apiUrl}/number/${rentalNumber}`).pipe(
      map(rental => this.mapDates(rental))
    );
  }

  /**
   * Cria uma nova locação
   */
  createVehicleRental(request: CreateVehicleRentalRequest): Observable<VehicleRental> {
    return this.http.post<VehicleRental>(this.apiUrl, request).pipe(
      map(rental => this.mapDates(rental)),
      tap(() => this.refreshVehicleRentals())
    );
  }

  /**
   * Atualiza uma locação
   */
  updateVehicleRental(id: number, request: UpdateVehicleRentalRequest): Observable<VehicleRental> {
    return this.http.put<VehicleRental>(`${this.apiUrl}/${id}`, request).pipe(
      map(rental => this.mapDates(rental)),
      tap(() => this.refreshVehicleRentals())
    );
  }

  /**
   * Exclui uma locação
   */
  deleteVehicleRental(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.refreshVehicleRentals())
    );
  }

  // ===== CONSULTAS AVANÇADAS =====

  /**
   * Obtém locações filtradas
   */
  getVehicleRentalsFiltered(filters: VehicleRentalFilter): Observable<VehicleRental[]> {
    let params = new HttpParams();

    Object.keys(filters).forEach(key => {
      if (filters[key as keyof VehicleRentalFilter] !== null &&
          filters[key as keyof VehicleRentalFilter] !== undefined &&
          filters[key as keyof VehicleRentalFilter] !== '') {
        params = params.set(key, filters[key as keyof VehicleRentalFilter]!.toString());
      }
    });

    return this.http.get<VehicleRental[]>(`${this.apiUrl}/filtered`, { params }).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações por status
   */
  getVehicleRentalsByStatus(status: string): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/status/${status}`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações ativas
   */
  getActiveVehicleRentals(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/active`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações pendentes
   */
  getPendingVehicleRentals(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/pending`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações finalizadas
   */
  getCompletedVehicleRentals(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/completed`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações por cliente
   */
  getVehicleRentalsByCustomer(customerId: number): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/customer/${customerId}`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações por documento do cliente
   */
  getVehicleRentalsByCustomerDocument(customerDocument: string): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/customer/document/${customerDocument}`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações por funcionário
   */
  getVehicleRentalsByStaff(staffId: number): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/staff/${staffId}`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações por período
   */
  getVehicleRentalsByDateRange(startDate: Date, endDate: Date): Observable<VehicleRental[]> {
    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http.get<VehicleRental[]>(`${this.apiUrl}/date-range`, { params }).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações que iniciam hoje
   */
  getVehicleRentalsStartingToday(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/starting-today`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações que terminam hoje
   */
  getVehicleRentalsEndingToday(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/ending-today`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém locações em atraso
   */
  getOverdueVehicleRentals(): Observable<VehicleRental[]> {
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/overdue`).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  // ===== OPERAÇÕES DE STATUS =====

  /**
   * Inicia uma locação
   */
  startVehicleRental(id: number, request: StartRentalRequest): Observable<VehicleRental> {
    return this.http.post<VehicleRental>(`${this.apiUrl}/${id}/start`, request).pipe(
      map(rental => this.mapDates(rental)),
      tap(() => this.refreshVehicleRentals())
    );
  }

  /**
   * Finaliza uma locação
   */
  endVehicleRental(id: number, request: EndRentalRequest): Observable<VehicleRental> {
    return this.http.post<VehicleRental>(`${this.apiUrl}/${id}/end`, request).pipe(
      map(rental => this.mapDates(rental)),
      tap(() => this.refreshVehicleRentals())
    );
  }


  /**
   * Cancela uma locação
   */
  cancelVehicleRental(id: number, request: CancelRentalRequest): Observable<VehicleRental> {
    return this.http.post<VehicleRental>(`${this.apiUrl}/${id}/cancel`, request).pipe(
      map(rental => this.mapDates(rental)),
      tap(() => this.refreshVehicleRentals())
    );
  }

  /**
   * Altera o status de uma locação
   */
  changeVehicleRentalStatus(id: number, newStatus: string, reason?: string): Observable<any> {
    const request = { status: newStatus, reason };
    return this.http.patch(`${this.apiUrl}/${id}/status`, request).pipe(
      tap(() => this.refreshVehicleRentals())
    );
  }

  

  // ===== PESQUISA E ESTATÍSTICAS =====

  /**
   * Pesquisa locações
   */
  searchVehicleRentals(searchTerm: string): Observable<VehicleRental[]> {
    const params = new HttpParams().set('term', searchTerm);
    return this.http.get<VehicleRental[]>(`${this.apiUrl}/search`, { params }).pipe(
      map(rentals => rentals.map(rental => this.mapDates(rental)))
    );
  }

  /**
   * Obtém estatísticas das locações
   */
  getVehicleRentalStatistics(): Observable<VehicleRentalStatistics> {
    return this.http.get<VehicleRentalStatistics>(`${this.apiUrl}/statistics`);
  }

  // ===== ITENS DA LOCAÇÃO =====

  /**
   * Obtém itens de uma locação
   */
  getVehicleRentalItems(rentalId: number): Observable<VehicleRentalItem[]> {
    return this.http.get<VehicleRentalItem[]>(`${this.apiUrl}/item/rental/${rentalId}`).pipe(
      map(items => items.map(item => this.mapItemDates(item)))
    );
  }

  /**
   * Obtém item específico de locação
   */
  getVehicleRentalItemById(id: number): Observable<VehicleRentalItem> {
    return this.http.get<VehicleRentalItem>(`${this.apiUrl}/item/${id}`).pipe(
      map(item => this.mapItemDates(item))
    );
  }

  /**
   * Inicia item de locação
   */
  startVehicleRentalItem(id: number, request: any): Observable<VehicleRentalItem> {
    return this.http.post<VehicleRentalItem>(`${this.apiUrl}/item/${id}/start`, request).pipe(
      map(item => this.mapItemDates(item))
    );
  }

  /**
   * Finaliza item de locação
   */
  endVehicleRentalItem(id: number, request: any): Observable<VehicleRentalItem> {
    return this.http.post<VehicleRentalItem>(`${this.apiUrl}/item/${id}/end`, request).pipe(
      map(item => this.mapItemDates(item))
    );
  }

  /**
   * Reporta dano em item de locação
   */
  reportVehicleRentalItemDamage(id: number, damageDescription: string): Observable<VehicleRentalItem> {
    const request = { damageDescription };
    return this.http.post<VehicleRentalItem>(`${this.apiUrl}/item/${id}/report-damage`, request).pipe(
      map(item => this.mapItemDates(item))
    );
  }

  /**
   * Reporta acessórios faltando em item de locação
   */
  reportVehicleRentalItemMissingAccessories(id: number, description: string): Observable<VehicleRentalItem> {
    const request = { missingAccessoriesDescription: description };
    return this.http.post<VehicleRentalItem>(`${this.apiUrl}/item/${id}/report-missing-accessories`, request).pipe(
      map(item => this.mapItemDates(item))
    );
  }

  // ===== HISTÓRICO DA LOCAÇÃO =====

  /**
   * Obtém histórico de uma locação
   */
  getVehicleRentalHistory(rentalId: number): Observable<VehicleRentalHistory[]> {
    return this.http.get<VehicleRentalHistory[]>(`${this.apiUrl}-history/rental/${rentalId}`).pipe(
      map(history => history.map(item => this.mapHistoryDates(item)))
    );
  }

  /**
   * Obtém histórico por ID
   */
  getVehicleRentalHistoryById(id: number): Observable<VehicleRentalHistory> {
    return this.http.get<VehicleRentalHistory>(`${this.apiUrl}-history/${id}`).pipe(
      map(item => this.mapHistoryDates(item))
    );
  }


  // ===== ANEXOS DA LOCAÇÃO =====


  // ===== UTILITÁRIOS =====

  /**
   * Gera um novo número de locação
   */
  generateRentalNumber(): Observable<{ rentalNumber: string }> {
    return this.http.get<{ rentalNumber: string }>(`${this.apiUrl}/generate-number`);
  }

  /**
   * Valida se um número de locação é único
   */
  validateRentalNumber(rentalNumber: string): Observable<{ isUnique: boolean }> {
    return this.http.get<{ isUnique: boolean }>(`${this.apiUrl}/validate-number/${rentalNumber}`);
  }

  // ===== MÉTODOS PRIVADOS =====

  /**
   * Converte strings de data em objetos Date para locações e mapeia PascalCase para camelCase
   */
  private mapDates(rental: any): VehicleRental {
    // Mapear campos de PascalCase para camelCase
    const mappedRental: any = {
      id: rental.Id,
      rentalNumber: rental.RentalNumber,
      startDate: rental.StartDate ? new Date(rental.StartDate) : undefined,
      endDate: rental.EndDate ? new Date(rental.EndDate) : undefined,
      actualStartDate: rental.ActualStartDate ? new Date(rental.ActualStartDate) : undefined,
      actualEndDate: rental.ActualEndDate ? new Date(rental.ActualEndDate) : undefined,
      priority: rental.Priority || 'NORMAL',
      status: rental.Status,
      customerId: rental.CustomerId,
      // Derivar nome do cliente quando não vier direto no DTO
      customerName: rental.CustomerName || (rental.Customer?.TradeName || rental.Customer?.CompanyName || rental.Customer?.FullName || ''),
      customerDocument: rental.CustomerDocument || rental.Customer?.DocumentNumber,
      customerEmail: rental.CustomerEmail || rental.Customer?.Email,
      customerPhone: rental.CustomerPhone || rental.Customer?.PrimaryPhone,
      driverName: rental.DriverName,
      driverDocument: rental.DriverDocument,
      driverLicenseNumber: rental.DriverLicenseNumber,
      driverLicenseExpiryDate: rental.DriverLicenseExpiryDate ? new Date(rental.DriverLicenseExpiryDate) : undefined,
      paymentStatus: rental.PaymentStatus,
      endMileage: rental.EndMileage,
      notes: rental.Notes,
      insuranceIncluded: rental.InsuranceIncluded || false,
      // Funcionário
      createdByStaffId: rental.CreatedByStaffId,
      assignedStaffId: rental.AssignedStaffId,
      staffName: rental.StaffName || rental.CreatedByStaff?.FullName || '',
      createdAt: rental.CreatedAt ? new Date(rental.CreatedAt) : new Date(),
      updatedAt: rental.UpdatedAt ? new Date(rental.UpdatedAt) : undefined,
      rentalItems: rental.RentalItems || [],
      rentalHistory: rental.RentalHistory || [],
      attachments: rental.Attachments || [],
      customer: rental.Customer
    };

    // Mapear datas dos itens
    if (mappedRental.rentalItems && Array.isArray(mappedRental.rentalItems)) {
      mappedRental.rentalItems = mappedRental.rentalItems.map((item: any) => this.mapItemDates(item));
    }

    // Mapear datas do histórico
    if (mappedRental.rentalHistory && Array.isArray(mappedRental.rentalHistory)) {
      mappedRental.rentalHistory = mappedRental.rentalHistory.map((item: any) => this.mapHistoryDates(item));
    }

    // Mapear datas dos anexos
    if (mappedRental.attachments && Array.isArray(mappedRental.attachments)) {
      mappedRental.attachments = mappedRental.attachments.map((item: any) => this.mapAttachmentDates(item));
    }

    return mappedRental as VehicleRental;
  }

  /**
   * Converte strings de data em objetos Date para itens e mapeia PascalCase para camelCase
   */
  private mapItemDates(item: any): VehicleRentalItem {
    const mappedItem: any = {
      id: item.Id,
      vehicleRentalId: item.VehicleRentalId,
      vehicleId: item.VehicleId,
      plateNumber: item.PlateNumber,
      brand: item.Brand,
      model: item.Model,
      modelYear: item.ModelYear,
      vehicleCategory: item.VehicleCategory,
      status: item.Status,
      hasDamage: item.HasDamage || false,
      missingAccessories: item.MissingAccessories || false,
      conditionAtStart: item.ConditionAtStart,
      conditionAtEnd: item.ConditionAtEnd,
      startMileage: item.StartMileage,
      endMileage: item.EndMileage,
      fuelLevelAtStart: item.FuelLevelAtStart,
      fuelLevelAtEnd: item.FuelLevelAtEnd,
      accessoriesAtStart: item.AccessoriesAtStart,
      accessoriesAtEnd: item.AccessoriesAtEnd,
      damageDescription: item.DamageDescription,
      notes: item.Notes,
      createdAt: item.CreatedAt ? new Date(item.CreatedAt) : new Date(),
      updatedAt: item.UpdatedAt ? new Date(item.UpdatedAt) : undefined,
      vehicle: item.Vehicle
    };

    return mappedItem as VehicleRentalItem;
  }



  /**
   * Atualiza a lista de locações
   */
  private refreshVehicleRentals(): void {
    this.getAllVehicleRentals().subscribe();
  }

  // ===== MÉTODOS DE FORMATAÇÃO =====

  /**
   * Formata o status para exibição
   */
  getStatusDisplayName(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Pendente': 'Pendente',
      'Ativa': 'Ativa',
      'Finalizada': 'Finalizada',
      'Cancelada': 'Cancelada'
    };
    return statusMap[status] || status;
  }

  /**
   * Obtém a cor do status
   */
  getStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      'Pendente': 'warning',
      'Ativa': 'success',
      'Finalizada': 'info',
      'Cancelada': 'secondary'
    };
    return colorMap[status] || 'secondary';
  }

  /**
   * Obtém o ícone do status
   */
  getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      'Pendente': 'clock',
      'Ativa': 'play-circle',
      'Finalizada': 'check-circle',
      'Cancelada': 'x-circle'
    };
    return iconMap[status] || 'circle';
  }

  /**
   * Formata prioridade para exibição
   */
  getPriorityDisplayName(priority: string): string {
    const priorityMap: { [key: string]: string } = {
      'Baixa': 'Baixa',
      'Normal': 'Normal',
      'Alta': 'Alta',
      'Urgente': 'Urgente'
    };
    return priorityMap[priority] || priority;
  }

  /**
   * Obtém a cor da prioridade
   */
  getPriorityColor(priority: string): string {
    const colorMap: { [key: string]: string } = {
      'Baixa': 'secondary',
      'Normal': 'info',
      'Alta': 'warning',
      'Urgente': 'danger'
    };
    return colorMap[priority] || 'secondary';
  }

  /**
   * Formata categoria de habilitação para exibição
   */
  getLicenseCategoryDisplayName(category: string): string {
    const categoryMap: { [key: string]: string } = {
      'A': 'Categoria A',
      'B': 'Categoria B',
      'C': 'Categoria C',
      'D': 'Categoria D',
      'E': 'Categoria E'
    };
    return categoryMap[category] || category;
  }

  // ===== ITENS =====

  /**
   * Obtém itens de uma locação específica
   */
  getVehicleRentalItemsByRental(vehicleRentalId: number): Observable<VehicleRentalItem[]> {
    return this.http.get<{
      Id: number;
      VehicleRentalId: number;
      VehicleId: number;
      PlateNumber: string;
      Brand: string;
      Model: string;
      ModelYear: number;
      VehicleCategory: string;
      Status: string;
      HasDamage: boolean;
      MissingAccessories: boolean;
      ConditionAtStart: string;
      ConditionAtEnd: string;
      StartMileage: number;
      EndMileage: number;
      FuelLevelAtStart: string;
      FuelLevelAtEnd: string;
      AccessoriesAtStart: string;
      AccessoriesAtEnd: string;
      DamageDescription: string;
      Notes: string;
      CreatedAt: string;
      UpdatedAt: string;
      Vehicle: any;
    }[]>(`${this.itemApiUrl}/rental/${vehicleRentalId}`).pipe(
      map(items => items.map(item => this.mapItemFromApi(item)))
    );
  }

  // ===== HISTÓRICO =====

  /**
   * Obtém histórico de uma locação
   */
  getVehicleRentalHistoryByRental(vehicleRentalId: number): Observable<VehicleRentalHistory[]> {
    return this.http.get<{
      Id: number;
      VehicleRentalId: number;
      Action: string;
      Status: string;
      PreviousStatus?: string;
      ActionDate: string;
      ActionTime?: string;
      Description?: string;
      Reason?: string;
      UserName?: string;
      StaffId?: number;
      CreatedAt: string;
    }[]>(`${this.historyApiUrl}/rental/${vehicleRentalId}`).pipe(
      map(history => history.map(item => this.mapHistoryFromApi(item)))
    );
  }

  /**
   * Obtém histórico ordenado de uma locação
   */
  getVehicleRentalHistoryByRentalOrdered(vehicleRentalId: number): Observable<VehicleRentalHistory[]> {
    return this.http.get<VehicleRentalHistory[]>(`${this.historyApiUrl}/rental/${vehicleRentalId}/ordered`).pipe(
      map(history => history.map(item => this.mapHistoryDates(item)))
    );
  }

  /**
   * Registra ação no histórico
   */
  logVehicleRentalAction(request: LogVehicleRentalActionRequest): Observable<VehicleRentalHistory> {
    return this.http.post<VehicleRentalHistory>(`${this.historyApiUrl}/log-action`, request).pipe(
      map(history => this.mapHistoryDates(history))
    );
  }

  /**
   * Registra mudança de status no histórico
   */
  logVehicleRentalStatusChange(request: LogVehicleRentalStatusChangeRequest): Observable<VehicleRentalHistory> {
    return this.http.post<VehicleRentalHistory>(`${this.historyApiUrl}/log-status-change`, request).pipe(
      map(history => this.mapHistoryDates(history))
    );
  }

  /**
   * Mapeia datas do histórico
   */
  private mapHistoryDates(history: VehicleRentalHistory): VehicleRentalHistory {
    return {
      ...history,
      actionDate: new Date(history.actionDate)
    };
  }

  /**
   * Mapeia dados de histórico da API (PascalCase) para o formato do frontend (camelCase)
   */
  private mapHistoryFromApi(apiHistory: {
    Id: number;
    VehicleRentalId: number;
    Action: string;
    Status: string;
    PreviousStatus?: string;
    ActionDate: string;
    ActionTime?: string;
    Description?: string;
    Reason?: string;
    UserName?: string;
    StaffId?: number;
    CreatedAt: string;
  }): VehicleRentalHistory {
    return {
      id: apiHistory.Id,
      vehicleRentalId: apiHistory.VehicleRentalId,
      action: apiHistory.Action,
      status: apiHistory.Status,
      previousStatus: apiHistory.PreviousStatus,
      actionDate: new Date(apiHistory.ActionDate),
      actionTime: apiHistory.ActionTime,
      description: apiHistory.Description,
      userName: apiHistory.UserName,
      staffId: apiHistory.StaffId,
      requiresApproval: false,
      isApproved: false,
      notificationSent: false,
      createdAt: new Date(apiHistory.CreatedAt)
    };
  }

  // ===== ANEXOS =====

  /**
   * Obtém anexos de uma locação
   */
  getVehicleRentalAttachments(vehicleRentalId: number): Observable<VehicleRentalAttachment[]> {
    return this.http.get<{
      Id: number;
      VehicleRentalId: number;
      FileName: string;
      FilePath: string;
      FileSize?: number;
      MimeType?: string;
      FileType?: string;
      FileExtension?: string;
      UploadedBy?: string;
      UploadDate: string;
      Category: string;
      SubCategory?: string;
      Description?: string;
      Notes?: string;
      Status: string;
      IsRequired?: boolean;
      IsPublic?: boolean;
      OriginalFileName?: string;
      Version?: number;
      IsLatestVersion?: boolean;
      CreatedAt: string;
      UpdatedAt?: string;
    }[]>(`${this.attachmentApiUrl}/rental/${vehicleRentalId}`).pipe(
      map(attachments => attachments.map(item => this.mapAttachmentFromApi(item)))
    );
  }

  /**
   * Obtém anexo por ID
   */
  getVehicleRentalAttachment(id: number): Observable<VehicleRentalAttachment> {
    return this.http.get<VehicleRentalAttachment>(`${this.attachmentApiUrl}/${id}`).pipe(
      map(attachment => this.mapAttachmentDates(attachment))
    );
  }

  /**
   * Cria anexo
   */
  createVehicleRentalAttachment(request: CreateVehicleRentalAttachmentRequest): Observable<VehicleRentalAttachment> {
    return this.http.post<VehicleRentalAttachment>(this.attachmentApiUrl, request).pipe(
      map(attachment => this.mapAttachmentDates(attachment))
    );
  }

  /**
   * Atualiza anexo
   */
  updateVehicleRentalAttachment(request: UpdateVehicleRentalAttachmentRequest): Observable<VehicleRentalAttachment> {
    return this.http.put<VehicleRentalAttachment>(`${this.attachmentApiUrl}/${request.id}`, request).pipe(
      map(attachment => this.mapAttachmentDates(attachment))
    );
  }

  /**
   * Remove anexo
   */
  deleteVehicleRentalAttachment(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.attachmentApiUrl}/${id}`);
  }

  /**
   * Upload de arquivo para blob storage
   */
  uploadFileToBlob(file: File, prefix?: string): Observable<{ url: string; fileName: string; container: string }> {
    const formData = new FormData();
    formData.append('file', file);

    let params = new HttpParams();
    if (prefix) {
      params = params.set('prefix', prefix);
    }

    return this.http.post<{ url: string; fileName: string; container: string }>(`${this.blobApiUrl}/upload`, formData, { params });
  }

  /**
   * Download de arquivo do blob storage
   */
  downloadFileFromBlob(fileName: string, prefix?: string): Observable<Blob> {
    let params = new HttpParams().set('fileName', fileName);
    if (prefix) {
      params = params.set('prefix', prefix);
    }
    return this.http.get(`${this.blobApiUrl}/download`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Lista arquivos do blob storage
   */
  listBlobFiles(prefix?: string, maxResults?: number): Observable<{ files: any[]; totalCount: number; prefix: string }> {
    let params = new HttpParams();
    if (prefix) params = params.set('prefix', prefix);
    if (maxResults) params = params.set('maxResults', maxResults.toString());

    return this.http.get<{ files: any[]; totalCount: number; prefix: string }>(`${this.blobApiUrl}/list`, { params });
  }

  /**
   * Remove arquivo do blob storage
   */
  deleteBlobFile(fileName: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.blobApiUrl}/delete/${fileName}`);
  }

  /**
   * Obtém categorias de anexos disponíveis
   */
  getAttachmentCategories(): AttachmentCategory[] {
    return [
      {
        value: 'CONTRATO',
        label: 'Contrato',
        description: 'Contrato de locação assinado',
        required: true,
        maxFiles: 1,
        allowedExtensions: ['.pdf', '.doc', '.docx']
      },
      {
        value: 'DOCUMENTOS_CLIENTE',
        label: 'Documentos do Cliente',
        description: 'CNH, RG, CPF do cliente',
        required: true,
        maxFiles: 5,
        allowedExtensions: ['.pdf', '.jpg', '.jpeg', '.png']
      },
      {
        value: 'DOCUMENTOS_VEICULO',
        label: 'Documentos do Veículo',
        description: 'CRLV, seguro, laudo técnico',
        required: false,
        maxFiles: 10,
        allowedExtensions: ['.pdf', '.jpg', '.jpeg', '.png']
      },
      {
        value: 'FOTOS_VEICULO',
        label: 'Fotos do Veículo',
        description: 'Fotos do veículo antes da locação',
        required: false,
        maxFiles: 20,
        allowedExtensions: ['.jpg', '.jpeg', '.png']
      },
      {
        value: 'OUTROS',
        label: 'Outros',
        description: 'Documentos diversos',
        required: false,
        maxFiles: 10,
        allowedExtensions: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.txt']
      }
    ];
  }

  /**
   * Mapeia datas dos anexos
   */
  private mapAttachmentDates(attachment: VehicleRentalAttachment): VehicleRentalAttachment {
    return {
      ...attachment,
      uploadDate: attachment.uploadDate ? new Date(attachment.uploadDate) : new Date(),
      createdAt: attachment.createdAt ? new Date(attachment.createdAt) : new Date(),
      updatedAt: attachment.updatedAt ? new Date(attachment.updatedAt) : undefined,
      expirationDate: attachment.expirationDate ? new Date(attachment.expirationDate) : undefined,
      backupDate: attachment.backupDate ? new Date(attachment.backupDate) : undefined,
      lastAccessedDate: attachment.lastAccessedDate ? new Date(attachment.lastAccessedDate) : undefined
    };
  }

  /**
   * Mapeia dados de item da API (PascalCase) para o formato do frontend (camelCase)
   */
  private mapItemFromApi(apiItem: {
    Id: number;
    VehicleRentalId: number;
    VehicleId: number;
    PlateNumber: string;
    Brand: string;
    Model: string;
    ModelYear: number;
    VehicleCategory: string;
    Status: string;
    HasDamage: boolean;
    MissingAccessories: boolean;
    ConditionAtStart: string;
    ConditionAtEnd: string;
    StartMileage: number;
    EndMileage: number;
    FuelLevelAtStart: string;
    FuelLevelAtEnd: string;
    AccessoriesAtStart: string;
    AccessoriesAtEnd: string;
    DamageDescription: string;
    Notes: string;
    CreatedAt: string;
    UpdatedAt: string;
    Vehicle: any;
  }): VehicleRentalItem {
    return {
      id: apiItem.Id,
      vehicleRentalId: apiItem.VehicleRentalId,
      vehicleId: apiItem.VehicleId,
      plateNumber: apiItem.PlateNumber,
      brand: apiItem.Brand,
      model: apiItem.Model,
      modelYear: apiItem.ModelYear,
      vehicleCategory: apiItem.VehicleCategory,
      status: apiItem.Status,
      hasDamage: apiItem.HasDamage,
      missingAccessories: apiItem.MissingAccessories,
      conditionAtStart: apiItem.ConditionAtStart,
      conditionAtEnd: apiItem.ConditionAtEnd,
      startMileage: apiItem.StartMileage,
      endMileage: apiItem.EndMileage,
      fuelLevelAtStart: apiItem.FuelLevelAtStart,
      fuelLevelAtEnd: apiItem.FuelLevelAtEnd,
      accessoriesAtStart: apiItem.AccessoriesAtStart,
      accessoriesAtEnd: apiItem.AccessoriesAtEnd,
      damageDescription: apiItem.DamageDescription,
      notes: apiItem.Notes,
      remainingDays: 0, // Calculado dinamicamente
      isOverdue: false, // Calculado dinamicamente
      isActive: apiItem.Status === 'ATIVO',
      canBeStarted: apiItem.Status === 'PENDENTE',
      canBeEnded: apiItem.Status === 'ATIVO',
      createdAt: new Date(apiItem.CreatedAt),
      updatedAt: apiItem.UpdatedAt ? new Date(apiItem.UpdatedAt) : undefined
    };
  }

  /**
   * Mapeia dados de anexo da API (PascalCase) para o formato do frontend (camelCase)
   */
  private mapAttachmentFromApi(apiAttachment: {
    Id: number;
    VehicleRentalId: number;
    FileName: string;
    FilePath: string;
    FileSize?: number;
    MimeType?: string;
    FileType?: string;
    FileExtension?: string;
    UploadedBy?: string;
    UploadDate: string;
    Category: string;
    SubCategory?: string;
    Description?: string;
    Notes?: string;
    Status: string;
    IsRequired?: boolean;
    IsPublic?: boolean;
    OriginalFileName?: string;
    Version?: number;
    IsLatestVersion?: boolean;
    CreatedAt: string;
    UpdatedAt?: string;
  }): VehicleRentalAttachment {
    return {
      id: apiAttachment.Id,
      vehicleRentalId: apiAttachment.VehicleRentalId,
      fileName: apiAttachment.FileName,
      filePath: apiAttachment.FilePath,
      fileSize: apiAttachment.FileSize,
      mimeType: apiAttachment.MimeType,
      fileType: apiAttachment.FileType,
      fileExtension: apiAttachment.FileExtension,
      uploadedBy: apiAttachment.UploadedBy,
      uploadDate: new Date(apiAttachment.UploadDate),
      category: apiAttachment.Category,
      subCategory: apiAttachment.SubCategory,
      description: apiAttachment.Description,
      notes: apiAttachment.Notes,
      status: apiAttachment.Status,
      isRequired: apiAttachment.IsRequired || false,
      isPublic: apiAttachment.IsPublic || false,
      originalFileName: apiAttachment.OriginalFileName,
      version: apiAttachment.Version || 1,
      isLatestVersion: apiAttachment.IsLatestVersion || true,
      isEncrypted: false,
      isExpired: false,
      isBackedUp: false,
      createdAt: new Date(apiAttachment.CreatedAt),
      updatedAt: apiAttachment.UpdatedAt ? new Date(apiAttachment.UpdatedAt) : undefined
    };
  }

  /**
   * Formata nível de combustível para exibição
   */
  getFuelLevelDisplayName(level: string): string {
    const levelMap: { [key: string]: string } = {
      'Vazio': 'Vazio',
      '1/4': '1/4 do tanque',
      '1/2': '1/2 do tanque',
      '3/4': '3/4 do tanque',
      'Cheio': 'Tanque cheio'
    };
    return levelMap[level] || level;
  }

  /**
   * Mapeia a resposta paginada (Items, Page, PageSize, TotalItems, TotalPages)
   * e converte datas/nomes para o modelo do frontend.
   */
  private mapPagedResponse(response: any): VehicleRentalPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems.map((r: any) => this.mapDates(r));

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages
    };
  }
}
